import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

/**
 * Footer links to the app's own legal/pricing pages (Terms, Privacy,
 * Pricing), served on the same app subdomain. Local copies live under
 * /legal/* so Paddle's domain-approval bot resolves every legal/pricing
 * link within this domain — external links to the marketing site failed
 * moderation.
 *
 * Paddle's domain-approval bot does not execute JavaScript, so every
 * publicly reachable page (login, root redirect screen) must carry these
 * links in the server-rendered HTML. Keep this component free of client
 * hooks/mount gates and never hide it behind a client-only auth check,
 * otherwise the domain check regresses to "offline or under construction".
 */
export function LegalFooterLinks({
  className,
  linkClassName,
}: {
  className?: string;
  linkClassName?: string;
}) {
  const t = useTranslations('account');

  // Same-domain internal routes — locale is resolved app-side (cookie/header),
  // so no locale path prefix is needed. Anchor tags stay in the SSR HTML for
  // the Paddle bot; internal navigation still works client-side.
  const footerLinks = [
    { href: '/legal/pricing', label: t('support.pricing') },
    { href: '/legal/terms', label: t('support.termsOfService') },
    { href: '/legal/privacy', label: t('support.privacyPolicy') },
    // Refunds are a section inside Terms; the #refunds anchor is emitted by
    // the terms renderer from an invisible [](#refunds) marker in the source
    // Markdown. Paddle requires a visible refund-policy link on the domain.
    { href: '/legal/terms#refunds', label: t('support.refundPolicy') },
  ];

  return (
    <footer
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 pb-6 text-list-item-subtitle',
        className
      )}
    >
      {footerLinks.map((link) => (
        <a
          key={link.href}
          href={link.href}
          className={cn(
            'underline underline-offset-2 transition-colors',
            linkClassName
          )}
        >
          {link.label}
        </a>
      ))}
    </footer>
  );
}
